import { FC, ImgHTMLAttributes, ReactNode } from 'react';
import { AdvertPhotoType } from '@edikk-edikk/country-frontend';

type PropsType = {
  photo: AdvertPhotoType;
  stub?: string;
  className?: string;
  isAllowAltFromPhoto?: boolean;
  isUse100?: boolean;
  width?: ImgHTMLAttributes<HTMLImageElement>['width'];
  height?: ImgHTMLAttributes<HTMLImageElement>['height'];
  alt?: ImgHTMLAttributes<HTMLImageElement>['alt'];
  loading?: ImgHTMLAttributes<HTMLImageElement>['loading'];
  fetchPriority?: ImgHTMLAttributes<HTMLImageElement>['fetchPriority'];
};

const PictureForPhoto: FC<PropsType> = ({
  photo,
  stub = null,
  className = null,
  isAllowAltFromPhoto = false,
  isUse100 = false,
  width = null,
  height = null,
  alt = null,
  loading = null,
  fetchPriority = null,
}) => {
  if (!photo && !stub) {
    return null;
  }

  const getAlt = (): string|null => {
    if (alt) {
      return alt;
    }

    return isAllowAltFromPhoto && photo && photo.alt ? photo.alt : null;
  };

  const renderSource = (media: string, srcSet: string[]): ReactNode => {
    if (srcSet.length === 0) {
      return null;
    }

    const srcSetAsString = srcSet.join(', ');
    return <source media={media} srcSet={srcSetAsString} />;
  };

  const renderSourceForLargeScreen = (): ReactNode => {
    if (!photo) {
      return null;
    }

    let urlX1 = photo.preview300Url;
    let urlX2 = photo.preview500Url;
    let urlX3 = photo.url;
    if (isUse100) {
      urlX1 = photo.preview200Url;
      urlX2 = photo.preview300Url;
      urlX3 = photo.preview500Url;
    }

    if (!urlX1 || !urlX2 || !urlX3) {
      return null;
    }

    const srcSet = [`${urlX1} 1x`, `${urlX2} 2x`, `${urlX3} 3x`];

    return renderSource('(min-width: 800px)', srcSet);
  };

  const renderSourceForSmallScreen = (): ReactNode => {
    if (!photo) {
      return null;
    }

    let urlX1 = photo.preview200Url;
    let urlX2 = photo.preview300Url;
    let urlX3 = photo.preview500Url;
    if (isUse100) {
      urlX1 = photo.preview100Url;
      urlX2 = photo.preview200Url;
      urlX3 = photo.preview300Url;
    }

    if (!urlX1 || !urlX2 || !urlX3) {
      return null;
    }

    const srcSet = [`${urlX1} 1x`, `${urlX2} 2x`, `${urlX3} 3x`];

    return renderSource('(max-width: 799px)', srcSet);
  };

  return (
    <picture>
      {renderSourceForLargeScreen()}
      {renderSourceForSmallScreen()}
      <img
        className={className}
        src={photo?.url || stub}
        alt={getAlt()}
        width={width}
        height={height}
        loading={loading}
        fetchPriority={fetchPriority}
      />
    </picture>
  );
};

export { PictureForPhoto };
